import React, { useState, useCallback, useRef, useEffect, memo } from 'react';
import {
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Box,
  Collapse,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
} from '@mui/material';
import {
  Save as SaveIcon,
  Edit as EditIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
} from '@mui/icons-material';

const isValueArray = (value: any): boolean => {
  return Array.isArray(value) && value.length > 0;
};

interface LanguageTextInputProps {
  initialValue?: string | string[] | null;
  onChange?: (value: string | string[]) => void;
  onSave?: (value: string | string[]) => void;
  disabled?: boolean;
  label?: string;
  size?: 'small' | 'medium';
}

const LanguageTextInput: React.FC<LanguageTextInputProps> = memo(
  ({ initialValue = '', onChange, onSave, disabled = false, label = '', size = 'small' }) => {
    const normalizeValue = (val: string | string[] | null | undefined): string[] => {
      if (val === null || val === undefined) return [];
      if (typeof val === 'string') return val ? [val] : [];
      return Array.isArray(val) ? val : [];
    };

    const inputRef = useRef<HTMLInputElement>(null);
    const [values, setValues] = useState<string[]>(normalizeValue(initialValue));
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [editingValue, setEditingValue] = useState<string>('');
    const [expanded, setExpanded] = useState(false);

    const isMounted = useRef(false);

    useEffect(() => {
      if (!isMounted.current) {
        setValues(normalizeValue(initialValue));
        isMounted.current = true;
      }
    }, [initialValue]);

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setEditingValue(newValue);

        if (onChange && editingIndex !== null) {
          const newValues = [...values];
          newValues[editingIndex] = newValue;
          const returnValue =
            isValueArray(initialValue) || newValues.length > 1 ? newValues : newValues[0] || '';
          onChange(returnValue);
        }
      },
      [onChange, values, editingIndex, initialValue]
    );

    const handleSave = useCallback(() => {
      if (editingIndex !== null) {
        const trimmedValue = editingValue.trim();
        if (!trimmedValue) {
          return;
        }
        const newValues = [...values];
        newValues[editingIndex] = trimmedValue;

        setValues(newValues);

        const returnValue =
          isValueArray(initialValue) || newValues.length > 1 ? newValues : newValues[0] || '';

        if (onSave) {
          onSave(returnValue);
        }

        setEditingIndex(null);
        setEditingValue('');
      }
    }, [editingIndex, editingValue, onSave, values, initialValue]);

    const handleEditClick = useCallback(
      (index: number) => {
        setEditingIndex(index);
        setEditingValue(values[index] || '');
        setExpanded(true);

        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }, 0);
      },
      [values]
    );

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
          handleSave();
        } else if (e.key === 'Escape') {
          setEditingIndex(null);
          setEditingValue('');
        }
      },
      [handleSave]
    );

    const toggleExpand = useCallback(() => {
      setExpanded(!expanded);
    }, [expanded]);

    if (values.length === 0) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <Box
            sx={{
              flexGrow: 1,
              px: 1,
              minHeight: '32px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            Empty
          </Box>
          <IconButton
            size="small"
            onClick={() => {
              setValues(['']);
              handleEditClick(0);
            }}
            disabled={disabled}
            sx={{ ml: 0.5 }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Box>
      );
    }

    if (editingIndex === null) {
      return (
        <Box sx={{ width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Tooltip
              title={values.length > 1 ? 'Click to expand/collapse' : values[0] || 'Empty'}
              arrow
              placement="top"
            >
              <Box
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flexGrow: 1,
                  px: 1,
                  minHeight: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  cursor: values.length > 1 ? 'pointer' : 'default',
                }}
                onClick={values.length > 1 ? toggleExpand : undefined}
              >
                {values.length > 1 ? (
                  <>
                    {values.length} values
                    <Box component="span" sx={{ ml: 1 }}>
                      {expanded ? (
                        <ExpandLessIcon fontSize="small" />
                      ) : (
                        <ExpandMoreIcon fontSize="small" />
                      )}
                    </Box>
                  </>
                ) : (
                  values[0] || 'Empty'
                )}
              </Box>
            </Tooltip>

            {values.length === 1 && (
              <IconButton
                size="small"
                onClick={() => handleEditClick(0)}
                disabled={disabled}
                sx={{ ml: 0.5 }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          {values.length > 1 && (
            <Collapse in={expanded}>
              <Paper variant="outlined" sx={{ mt: 1, maxHeight: '200px', overflow: 'auto' }}>
                <List dense disablePadding>
                  {values.map((value, index) => (
                    <React.Fragment key={index}>
                      {index > 0 && <Divider />}
                      <ListItem
                        secondaryAction={
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => handleEditClick(index)}
                            disabled={disabled}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        }
                      >
                        <ListItemText
                          primary={value}
                          sx={{
                            '& .MuiListItemText-primary': {
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            },
                          }}
                        />
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              </Paper>
            </Collapse>
          )}
        </Box>
      );
    }

    // Edit mode
    return (
      <TextField
        inputRef={inputRef}
        size={size}
        value={editingValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        label={label || `Editing value ${editingIndex + 1} of ${values.length}`}
        variant="outlined"
        fullWidth
        disabled={disabled}
        autoFocus
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" onClick={handleSave} edge="end">
                <SaveIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    );
  }
);

LanguageTextInput.displayName = 'LanguageTextInput';

export { LanguageTextInput };
